import {
  AgoraData,
  FEATURE_CALL,
  FEATURE_SOUND,
  FEATURE_VIDEO,
  PreferredMediaDevices,
  PreviewDeviceType,
  PreviewError,
  PreviewErrorReason,
  VideoConferenceProvider,
} from '../definitions';
import AgoraRTC, {
  DeviceInfo,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  RemoteStreamFallbackType,
} from 'agora-rtc-sdk-ng';
import { NgZone } from '@angular/core';
import { AgoraSettings } from '../props';
import { BehaviorSubject, Subject } from 'rxjs';
import { AIDenoiserExtension, AIDenoiserProcessor } from 'agora-extension-ai-denoiser';
import { AgoraApiProviderService } from '@project/data-providers';

export type AgoraMediaType = 'audio' | 'video' | 'datachannel';

const LOCAL_MIC_VOLUME_POLLING_INTERVAL_MS = 150;

export class AgoraProvider extends VideoConferenceProvider {
  public name = 'Agora Provider';

  private localMicrophoneAudioTrack: IMicrophoneAudioTrack;
  private localCameraVideoTrack: ICameraVideoTrack;

  private client: IAgoraRTCClient = AgoraRTC.createClient(AgoraSettings.clientOptions);
  private denoiser = new AIDenoiserExtension({ assetsPath: AgoraSettings.denoiserPath });
  private denoiserProcessor: AIDenoiserProcessor;
  private hasCamera: boolean;
  private isCameraEnabled = true;
  private hasSupportDenoiser: boolean;
  private enableSTT: boolean;
  private enableSTTRecordAudio: boolean;

  private remoteVideoBehaviorSubject$ = new BehaviorSubject<boolean>(false);
  private remoteAudioBehaviorSubject$ = new BehaviorSubject<boolean>(false);

  public remoteVideoObservable = this.remoteVideoBehaviorSubject$.asObservable();
  public remoteAudioObservable = this.remoteAudioBehaviorSubject$.asObservable();

  private localVideoBehaviorSubject$ = new BehaviorSubject<boolean>(false);
  private localAudioBehaviorSubject$ = new BehaviorSubject<boolean>(false);

  public localVideoObservable = this.localVideoBehaviorSubject$.asObservable();
  public localAudioObservable = this.localAudioBehaviorSubject$.asObservable();

  private remoteFallbackSubject = new BehaviorSubject<boolean>(false);
  public remoteFallbackObservable = this.remoteFallbackSubject.asObservable();

  private previewErrorSubject$ = new BehaviorSubject<PreviewError | null>(null);
  public previewErrorObservable = this.previewErrorSubject$.asObservable();

  private localMicVolumeSubject$ = new BehaviorSubject<number>(0);
  public localMicVolumeObservable = this.localMicVolumeSubject$.asObservable();

  private devicesChangedSubject$ = new Subject<void>();
  public devicesChangedObservable = this.devicesChangedSubject$.asObservable();

  private localMicVolumeInterval: ReturnType<typeof setInterval>;
  private selectedSpeakerId: string;
  private preferredDevices: PreferredMediaDevices = {};
  private localVideoContainer: HTMLElement | null = null;

  private isJoined = false;
  private areDeviceListenersRegistered = false;
  private joinPromise: Promise<void> | null = null;

  constructor(
    private agoraData: AgoraData,
    private agoraApiService: AgoraApiProviderService,
    private sttEnabled: boolean,
    private ngZone: NgZone,
  ) {
    super();
    this.setup();
    this.registerDenoiser();
    this.enableLogs();
    this.setup();
  }

  async join(): Promise<void> {
    this.joinPromise = this.performJoin();

    try {
      await this.joinPromise;
    } finally {
      this.joinPromise = null;
    }
  }

  private async performJoin(): Promise<void> {
    await this.enableMediaStreamFallback();
    await this.joinChannel(this.agoraData);
    this.isJoined = true;
  }

  async left(): Promise<void> {
    try {
      // Se o usuário desliga a chamada antes de join() terminar, espera o join em curso se
      // resolver (ou falhar) antes de decidir se há algo para de fato encerrar na Agora.
      await this.joinPromise?.catch(() => undefined);

      if (!this.isJoined) {
        return;
      }

      await this.leaveChannel();
      this.client.removeAllListeners();
      this.isJoined = false;
    } catch (error) {
      console.error('failed to leave videoconference channel', error);
    }
  }

  async createLocalPreviewTracks(): Promise<void> {
    this.registerDeviceListeners();
    await this.acquireLocalPreviewTracks();
  }

  async stopLocalPreview(): Promise<void> {
    this.previewErrorSubject$.next(null);
    this.stopLocalMicVolumeMonitoring();
    this.unregisterDeviceListeners();

    if (this.localCameraVideoTrack) {
      this.localCameraVideoTrack.stop();
      this.localCameraVideoTrack.close();
      this.localCameraVideoTrack = undefined;
    }

    if (this.localMicrophoneAudioTrack) {
      this.localMicrophoneAudioTrack.stop();
      this.localMicrophoneAudioTrack.close();
      this.localMicrophoneAudioTrack = undefined;
    }

    this.localVideoBehaviorSubject$.next(false);
    this.localAudioBehaviorSubject$.next(false);
  }

  private async acquireLocalPreviewTracks(): Promise<void> {
    if (!this.localMicrophoneAudioTrack) {
      try {
        this.localMicrophoneAudioTrack = await this.createMicrophoneAudioTracker();
      } catch (error) {
        this.previewErrorSubject$.next(this.mapToPreviewError(PreviewDeviceType.Microphone, error));
        return;
      }
    }

    if (!this.localCameraVideoTrack) {
      try {
        this.localCameraVideoTrack = await this.createCameraVideoTracker();
      } catch (error) {
        this.previewErrorSubject$.next(this.mapToPreviewError(PreviewDeviceType.Camera, error));
        return;
      }
    }

    this.hasCamera = true;
    this.previewErrorSubject$.next(null);
    this.playLocalPreviewVideo();
    this.startLocalMicVolumeMonitoring();
  }

  private startLocalMicVolumeMonitoring(): void {
    if (this.localMicVolumeInterval) {
      return;
    }

    this.localMicVolumeInterval = setInterval(() => {
      if (!this.localMicrophoneAudioTrack || this.localMicrophoneAudioTrack.muted) {
        this.localMicVolumeSubject$.next(0);
        return;
      }

      this.localMicVolumeSubject$.next(this.localMicrophoneAudioTrack.getVolumeLevel());
    }, LOCAL_MIC_VOLUME_POLLING_INTERVAL_MS);
  }

  private stopLocalMicVolumeMonitoring(): void {
    if (this.localMicVolumeInterval) {
      clearInterval(this.localMicVolumeInterval);
      this.localMicVolumeInterval = undefined;
    }

    this.localMicVolumeSubject$.next(0);
  }

  private playLocalPreviewVideo(): void {
    const localContainer = this.setupContainer('local');

    if (localContainer && this.localCameraVideoTrack) {
      this.localCameraVideoTrack.play(localContainer, AgoraSettings.videoConfig);
    }

    this.isCameraEnabled = true;
    this.localVideoBehaviorSubject$.next(true);
    this.localAudioBehaviorSubject$.next(true);
  }

  private registerDeviceListeners(): void {
    if (this.areDeviceListenersRegistered) {
      return;
    }

    this.areDeviceListenersRegistered = true;

    AgoraRTC.on('camera-changed', this.onCameraChanged);
    AgoraRTC.on('microphone-changed', this.onMicrophoneChanged);
    AgoraRTC.on('playback-device-changed', this.onPlaybackDeviceChanged);
  }

  private unregisterDeviceListeners(): void {
    if (!this.areDeviceListenersRegistered) {
      return;
    }

    this.areDeviceListenersRegistered = false;

    AgoraRTC.off('camera-changed', this.onCameraChanged);
    AgoraRTC.off('microphone-changed', this.onMicrophoneChanged);
    AgoraRTC.off('playback-device-changed', this.onPlaybackDeviceChanged);
  }

  // The SDK listens to the browser's `devicechange` from outside the Angular zone, so its device
  // events arrive there too: without running them in the zone, the preview recovers the camera
  // but the screen keeps showing the error until something else triggers change detection.
  private readonly onCameraChanged = (info: DeviceInfo): Promise<void> =>
    this.ngZone.run(() => this.handleCameraChanged(info));

  private readonly onMicrophoneChanged = (info: DeviceInfo): Promise<void> =>
    this.ngZone.run(() => this.handleMicrophoneChanged(info));

  private readonly onPlaybackDeviceChanged = (): void => this.ngZone.run(() => this.handlePlaybackDeviceChanged());

  private readonly handleCameraChanged = async (info: DeviceInfo): Promise<void> => {
    if (await this.retryPreviewAfterError()) {
      return;
    }

    if (this.localCameraVideoTrack && this.isActiveDeviceRemoved(info, this.localCameraVideoTrack)) {
      const [fallbackCamera] = await AgoraRTC.getCameras(true);

      if (fallbackCamera) {
        await this.localCameraVideoTrack
          .setDevice(fallbackCamera.deviceId)
          .catch((error) => console.error('failed to fall back to another camera', error));
      }
    }

    this.devicesChangedSubject$.next();
  };

  private readonly handleMicrophoneChanged = async (info: DeviceInfo): Promise<void> => {
    if (await this.retryPreviewAfterError()) {
      return;
    }

    if (this.localMicrophoneAudioTrack && this.isActiveDeviceRemoved(info, this.localMicrophoneAudioTrack)) {
      const [fallbackMicrophone] = await AgoraRTC.getMicrophones(true);

      if (fallbackMicrophone) {
        await this.localMicrophoneAudioTrack
          .setDevice(fallbackMicrophone.deviceId)
          .catch((error) => console.error('failed to fall back to another microphone', error));
      }
    }

    this.devicesChangedSubject$.next();
  };

  private readonly handlePlaybackDeviceChanged = (): void => {
    this.devicesChangedSubject$.next();
  };

  private async retryPreviewAfterError(): Promise<boolean> {
    if (this.isJoined || this.previewErrorSubject$.value === null) {
      return false;
    }

    await this.acquireLocalPreviewTracks();
    this.devicesChangedSubject$.next();

    return true;
  }

  private isActiveDeviceRemoved(info: DeviceInfo, track: ICameraVideoTrack | IMicrophoneAudioTrack): boolean {
    return info.state === 'INACTIVE' && info.device.deviceId === this.getTrackDeviceId(track);
  }

  private getTrackDeviceId(track: ICameraVideoTrack | IMicrophoneAudioTrack | undefined): string | null {
    return track?.getMediaStreamTrack()?.getSettings().deviceId ?? null;
  }

  private mapToPreviewError(device: PreviewDeviceType, error: unknown): PreviewError {
    // AgoraRTCErrorCode only exists in the SDK typings (`declare enum`): the SDK bundle doesn't
    // export it, so comparing against its members throws at runtime. The error carries the code
    // as a plain string.
    const code = (error as { code?: string })?.code;

    switch (code) {
      case 'NOT_READABLE':
        return { device, reason: PreviewErrorReason.DeviceInUse };
      case 'PERMISSION_DENIED':
        return { device, reason: PreviewErrorReason.PermissionDenied };
      case 'DEVICE_NOT_FOUND':
        return { device, reason: PreviewErrorReason.DeviceNotFound };
      default:
        return { device, reason: PreviewErrorReason.Unknown };
    }
  }

  features(): string[] {
    return [FEATURE_VIDEO, FEATURE_SOUND, FEATURE_CALL];
  }

  getRemoteParticipants(): (string | number)[] {
    return this.client.remoteUsers.map((user) => user.uid);
  }

  async toggleVideo(): Promise<void> {
    if (!this.hasCamera) {
      return Promise.resolve();
    }

    this.isCameraEnabled = !this.isCameraEnabled;

    await this.localCameraVideoTrack.setEnabled(this.isCameraEnabled);
    this.localVideoBehaviorSubject$.next(this.isCameraEnabled);

    if (this.isCameraEnabled) {
      this.setLocalVideoContainer(this.localVideoContainer);
    }
  }

  async toggleSound(): Promise<void> {
    const isMuted = this.localMicrophoneAudioTrack.muted;

    await this.localMicrophoneAudioTrack.setMuted(!isMuted);
    this.localAudioBehaviorSubject$.next(isMuted);
  }

  private setup() {
    this.enableSTT = this.sttEnabled;
    this.enableSTTRecordAudio = this.sttEnabled;
  }

  private async joinChannel(data: AgoraData): Promise<void> {
    this.handlerEvents();
    this.registerDeviceListeners();

    const cameras = await AgoraRTC.getCameras();
    this.hasCamera = cameras.length > 0;

    if (this.hasCamera) {
      await this.startVideoAndAudio(data);
    } else {
      await this.startOnlyAudio(data);
    }

    console.log(`init channel with stt: ${this.enableSTT} with record audio ${this.enableSTTRecordAudio}`);

    if (this.enableSTT) {
      this.agoraApiService.startSpeechToText(data.channel, this.enableSTTRecordAudio).subscribe({
        next: () => {
          console.log(`STT started`);
        },
        error: (e) => {
          console.error(`STT error`, e);
        },
      });
    }
  }

  private async startVideoAndAudio(data: AgoraData): Promise<void> {
    if (!this.localMicrophoneAudioTrack) {
      this.localMicrophoneAudioTrack = await this.createMicrophoneAudioTracker();
    }

    if (!this.localCameraVideoTrack) {
      this.localCameraVideoTrack = await this.createCameraVideoTracker();
    }

    if (this.hasSupportDenoiser) {
      await this.applyProcessDenoiser();
    }

    try {
      await this.client.join(data.appId, data.channel, data.token, data.uid);
      await this.client.publish([this.localMicrophoneAudioTrack, this.localCameraVideoTrack]);
    } catch (error) {
      await this.stopLocalPreview();
      throw error;
    }

    const localContainer = this.setupContainer('local');

    this.localCameraVideoTrack.play(localContainer, AgoraSettings.videoConfig);
    this.isCameraEnabled = true;
    this.localVideoBehaviorSubject$.next(true);

    this.localAudioBehaviorSubject$.next(true);
    this.startLocalMicVolumeMonitoring();
  }

  private async startOnlyAudio(data: AgoraData): Promise<void> {
    if (!this.localMicrophoneAudioTrack) {
      this.localMicrophoneAudioTrack = await this.createMicrophoneAudioTracker();
    }

    if (this.hasSupportDenoiser) {
      await this.applyProcessDenoiser();
    }

    try {
      await this.client.join(data.appId, data.channel, data.token, data.uid);
      await this.client.publish(this.localMicrophoneAudioTrack);
    } catch (error) {
      await this.stopLocalPreview();
      throw error;
    }

    this.localVideoBehaviorSubject$.next(false);
    this.remoteVideoBehaviorSubject$.next(false);

    this.localAudioBehaviorSubject$.next(true);
    this.startLocalMicVolumeMonitoring();
  }

  private async leaveChannel(): Promise<void> {
    this.stopLocalMicVolumeMonitoring();
    this.unregisterDeviceListeners();

    if (this.hasSupportDenoiser && this.denoiserProcessor) {
      await this.destroyProcessDenoiser();
    }

    if (this.hasCamera) {
      await this.client.unpublish([this.localMicrophoneAudioTrack, this.localCameraVideoTrack]);
    } else {
      await this.client.unpublish(this.localMicrophoneAudioTrack);
    }

    this.localMicrophoneAudioTrack.stop();
    this.localMicrophoneAudioTrack.close();

    if (this.hasCamera) {
      this.localCameraVideoTrack.stop();
      this.localCameraVideoTrack.close();
    }

    await this.client.leave();

    if (this.enableSTT) {
      this.agoraApiService.stopSpeechToText(this.agoraData.channel, this.enableSTTRecordAudio).subscribe({
        next: () => {
          console.log(`STT stopped`);
        },
        error: (e) => {
          console.error(`STT error`, e);
        },
      });
    }
  }

  private setupContainer(type: string): HTMLElement {
    switch (type) {
      case 'local':
        return this.localVideoContainer ?? document.getElementById(AgoraSettings.container.localName);
      case 'remote':
        return document.getElementById(AgoraSettings.container.remoteName);
      default:
        null;
    }
  }

  async enableMyRemoteVideo(): Promise<void> {
    if (!this.hasCamera) {
      return Promise.resolve();
    }
    await this.client.publish(this.localCameraVideoTrack);
  }

  async disableMyRemoteVideo(): Promise<void> {
    if (!this.hasCamera) {
      return Promise.resolve();
    }
    await this.client.unpublish(this.localCameraVideoTrack);
  }

  private handlerEvents() {
    this.client.on('user-published', async (user, mediaType) => {
      await this.handlerUserPublished(user, mediaType);
    });

    this.client.on('user-unpublished', async (user, mediaType) => {
      await this.handlerUserUnpublished(user, mediaType);
    });
  }

  private async handlerUserPublished(user: IAgoraRTCRemoteUser, mediaType: AgoraMediaType): Promise<void> {
    await this.client.subscribe(user, mediaType);

    if (mediaType === 'video') {
      const remoteContainer = this.setupContainer('remote');
      user.videoTrack.play(remoteContainer, AgoraSettings.videoConfig);

      this.remoteVideoBehaviorSubject$.next(true);
    }

    if (mediaType === 'audio') {
      this.remoteAudioBehaviorSubject$.next(true);

      if (this.selectedSpeakerId) {
        try {
          await user.audioTrack.setPlaybackDevice(this.selectedSpeakerId);
        } catch (error) {
          console.error('failed to set playback device', error);
        }
      }

      user.audioTrack.play();
    }
  }

  private async handlerUserUnpublished(user: IAgoraRTCRemoteUser, mediaType: AgoraMediaType): Promise<void> {
    await this.client.unsubscribe(user, mediaType);

    if (mediaType === 'video') {
      this.remoteVideoBehaviorSubject$.next(false);
    }

    if (mediaType === 'audio') {
      this.remoteAudioBehaviorSubject$.next(false);
    }
  }

  /**
   *
   * level – The output log level.
   *    0: DEBUG. Output all API logs.
   *    1: INFO. Output logs of the INFO, WARNING and ERROR level.
   *    2: WARNING. Output logs of the WARNING and ERROR level.
   *    3: ERROR. Output logs of the ERROR level.
   *    4: NONE. Do not output any log.
   *
   * @see https://docs.agora.io/en/help/integration-issues/log/#web
   */
  private enableLogs() {
    AgoraRTC.enableLogUpload();
    AgoraRTC.setLogLevel(1);
  }

  /**
   *
   * AI Noise Suppression enables you to suppress hundreds of types of noise and reduce distortion
   * in human voices when multiple people speak at the same time. In scenarios such as online meetings,
   * online chat rooms, video consultations with doctors, and online gaming, AI Noise Suppression makes
   * virtual communication as smooth as face-to-face interaction.
   *
   * @see https://docs.agora.io/en/video-calling/advanced-features/ai-noise-suppression?platform=web
   * @private
   */
  private registerDenoiser() {
    this.hasSupportDenoiser = this.denoiser.checkCompatibility();

    if (!this.hasSupportDenoiser) {
      console.error('Does not support AI Denoiser!');
      return;
    }

    AgoraRTC.registerExtensions([this.denoiser]);
  }

  private async applyProcessDenoiser(): Promise<void> {
    this.denoiserProcessor = this.denoiser.createProcessor();
    this.handlerDenoiserEvents(this.denoiserProcessor);

    this.localMicrophoneAudioTrack
      .pipe(this.denoiserProcessor)
      .pipe(this.localMicrophoneAudioTrack.processorDestination);

    await this.denoiserProcessor.enable();
  }

  private handlerDenoiserEvents(processor: AIDenoiserProcessor) {
    processor.on('overload', async () => {
      console.log('AI Denoiser overload!!!');

      // Switch from AI noise suppression to stationary noise suppression
      await processor.setMode('STATIONARY_NS');

      await processor.disable(); // Disable AI noise suppression
    });

    processor.on('pipeerror', async (error) => {
      console.error('failed to pipe AI Denoiser processor', error);

      processor.unpipe();
      this.localMicrophoneAudioTrack.unpipe();
      this.localMicrophoneAudioTrack.pipe(this.localMicrophoneAudioTrack.processorDestination);
    });
  }

  private async destroyProcessDenoiser(): Promise<void> {
    try {
      this.denoiserProcessor.unpipe();
    } catch (error) {
      console.error('failed to unpipe AI Denoiser processor', error);
    }

    try {
      this.localMicrophoneAudioTrack.unpipe();
      this.localMicrophoneAudioTrack.pipe(this.localMicrophoneAudioTrack.processorDestination);
    } catch (error) {
      console.error('failed to reset microphone track pipeline', error);
    }
  }

  private async createCameraVideoTracker(): Promise<ICameraVideoTrack> {
    const cameraId = await this.resolveAvailableDeviceId(this.preferredDevices.cameraId, () =>
      AgoraRTC.getCameras(true),
    );

    return AgoraRTC.createCameraVideoTrack(
      cameraId ? { ...AgoraSettings.videoTrackerConfig, cameraId } : AgoraSettings.videoTrackerConfig,
    );
  }

  private async createMicrophoneAudioTracker(): Promise<IMicrophoneAudioTrack> {
    const microphoneId = await this.resolveAvailableDeviceId(this.preferredDevices.microphoneId, () =>
      AgoraRTC.getMicrophones(true),
    );

    return AgoraRTC.createMicrophoneAudioTrack(
      microphoneId ? { ...AgoraSettings.audioTrackerConfig, microphoneId } : AgoraSettings.audioTrackerConfig,
    );
  }

  private async resolveAvailableDeviceId(
    preferredDeviceId: string | null | undefined,
    listDevices: () => Promise<MediaDeviceInfo[]>,
  ): Promise<string | undefined> {
    if (!preferredDeviceId) {
      return undefined;
    }

    try {
      const devices = await listDevices();

      return devices.some((device) => device.deviceId === preferredDeviceId) ? preferredDeviceId : undefined;
    } catch {
      return undefined;
    }
  }

  setPreferredDevices(devices: PreferredMediaDevices): void {
    this.preferredDevices = { ...devices };
    this.selectedSpeakerId = devices.speakerId ?? undefined;
  }

  getActiveDevices(): PreferredMediaDevices {
    return {
      cameraId: this.getTrackDeviceId(this.localCameraVideoTrack),
      microphoneId: this.getTrackDeviceId(this.localMicrophoneAudioTrack),
      speakerId: this.selectedSpeakerId ?? null,
    };
  }

  setLocalVideoContainer(container: HTMLElement | null): void {
    this.localVideoContainer = container;

    if (!this.localCameraVideoTrack || !this.isCameraEnabled || (!container && !this.isJoined)) {
      return;
    }

    const target = this.setupContainer('local');

    if (!target) {
      return;
    }

    try {
      this.localCameraVideoTrack.stop();
      this.localCameraVideoTrack.play(target, AgoraSettings.videoConfig);
    } catch (error) {
      console.error('failed to move local video to another container', error);
    }
  }

  async listCameras(): Promise<MediaDeviceInfo[]> {
    return AgoraRTC.getCameras();
  }

  async listMicrophones(): Promise<MediaDeviceInfo[]> {
    return AgoraRTC.getMicrophones();
  }

  async listSpeakers(): Promise<MediaDeviceInfo[]> {
    return AgoraRTC.getPlaybackDevices();
  }

  async switchCamera(deviceId: string): Promise<void> {
    await this.localCameraVideoTrack?.setDevice(deviceId);
  }

  async switchMicrophone(deviceId: string): Promise<void> {
    await this.localMicrophoneAudioTrack?.setDevice(deviceId);
  }

  switchSpeaker(deviceId: string): void {
    this.selectedSpeakerId = deviceId;

    this.client.remoteUsers.forEach((user) => {
      user.audioTrack
        ?.setPlaybackDevice(deviceId)
        .catch((error) => console.error('failed to set playback device', error));
    });
  }

  /**
   * In situations with poor network conditions, the quality of audio and video may decrease.
   * When the network is unstable, and it's challenging to maintain both audio and video quality,
   * the Video SDK automatically adjusts by switching the video stream to a lower quality or,
   * if necessary, switches to audio-only mode to ensure better audio quality.
   *
   * @see https://docs.agora.io/en/video-calling/advanced-features/media-stream-fallback?platform=web
   */
  private async enableMediaStreamFallback(): Promise<void> {
    try {
      await this.client.enableDualStream();
    } catch (error) {
      console.error('failed to enable dual stream fallback', error);
    }
  }

  /**
   * Enable fallback for use audio only
   */
  private async enableFallbackUser(uid: number | string): Promise<void> {
    await this.client.setStreamFallbackOption(uid, RemoteStreamFallbackType.AUDIO_ONLY);
    await this.disableMyRemoteVideo();
    this.remoteFallbackSubject.next(true);
    console.log(`enableFallbackUser ${uid} RemoteStreamFallbackType.AUDIO_ONLY`);
  }

  /**
   * Disable fallback
   */
  private async disableFallback(uid: number | string): Promise<void> {
    await this.client.setStreamFallbackOption(uid, RemoteStreamFallbackType.DISABLE);
    await this.enableMyRemoteVideo();
    this.remoteFallbackSubject.next(false);
    console.log(`disableFallbackUser ${uid} RemoteStreamFallbackType.DISABLED`);
  }
}
